// src/pages/SeniorAnalystDashboard.tsx - With Admin Dashboard Cards

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  FileText, 
  Bell, 
  ClipboardCheck,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Database,
  Plus,
  Loader2,
  Search,
  Eye,
  Building2,
  MapPin,
  RefreshCw,
  ChevronRight,
  BarChart3,
  Target,
  Users,
  UserCheck,
  Shield,
  Activity,
  Calendar,
  CheckSquare,
  XCircle,
  Briefcase,
  Flag,
  Star,
  GraduationCap,
  Award,
  Trash2,
  Filter,
  UserCog
} from 'lucide-react';

// Helper function to format currency
const formatCurrency = (value: number): string => {
  if (!value) return 'KES 0';
  if (value >= 1_000_000_000) return `KES ${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `KES ${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `KES ${(value / 1_000).toFixed(1)}K`;
  return `KES ${value.toLocaleString()}`;
};

interface Analyst {
  id: string;
  name: string;
  email: string;
  role: string;
  analyst_type?: 'business' | 'problem_bank' | null;
  assignedProblemsCount: number;
  completedProblemsCount: number;
  averageCompletionTime: number;
  currentWorkload: number;
}

interface SquadAssignment {
  id: string;
  problemId: string;
  problemTitle: string;
  squadId: string;
  squadName: string;
  assignedAt: string;
  targetDate?: string;
  status: string;
  progress: number;
  milestones: any[];
  assignedBy?: string;
  assignedByName?: string;
}

// Helper function for initials
const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Stat Card Component
function StatCard({ title, value, description, icon: Icon, color, onClick }: any) {
  return (
    <Card 
      className={`${onClick ? 'cursor-pointer hover:shadow-md transition-all' : 'hover:shadow-md transition-shadow'}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          </div>
          <div className={`${color} p-2 rounded-lg text-white`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SeniorAnalystDashboard() {
  const { user } = useAuth();
  const { pitches } = useData();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [allProblems, setAllProblems] = useState<any[]>([]);
  const [assignedToSquads, setAssignedToSquads] = useState<SquadAssignment[]>([]);
  const [analysts, setAnalysts] = useState<Analyst[]>([]);
  const [pendingSubmissions, setPendingSubmissions] = useState<any[]>([]);
  const [isLoadingProblems, setIsLoadingProblems] = useState(true);
  const [isLoadingAnalysts, setIsLoadingAnalysts] = useState(true);
  
  // Deletion requests state
  const [deletionRequests, setDeletionRequests] = useState<any[]>([]);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewDecision, setReviewDecision] = useState<'approve' | 'reject'>('approve');
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Assignment card state - For Verification (Prospect problems)
  const [prospectProblems, setProspectProblems] = useState<any[]>([]);
  const [verifiedProblems, setVerifiedProblems] = useState<any[]>([]);
  const [problemBankAnalysts, setProblemBankAnalysts] = useState<any[]>([]);
  
  // Assignment states
  const [selectedProblemForVerification, setSelectedProblemForVerification] = useState<string>('');
  const [selectedAnalystForVerification, setSelectedAnalystForVerification] = useState<string>('');
  const [assigningVerification, setAssigningVerification] = useState(false);
  
  const [selectedProblemForSolution, setSelectedProblemForSolution] = useState<string>('');
  const [selectedAnalystForSolution, setSelectedAnalystForSolution] = useState<string>('');
  const [assigningSolution, setAssigningSolution] = useState(false);

  useEffect(() => {
    loadAllData();
    loadDeletionRequests();
  }, [user]);

  const loadAllData = async () => {
    await Promise.all([
      loadAllProblems(),
      loadAnalysts(),
      loadPendingSubmissions(),
      loadSquadAssignments()
    ]);
  };

  const loadAllProblems = async () => {
    setIsLoadingProblems(true);
    try {
      const { data: problemsData, error } = await supabase
        .from('problem_bank')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const parsedProblems = (problemsData || []).map(problem => ({
        ...problem,
        anchor: typeof problem.anchor === 'string' ? JSON.parse(problem.anchor) : problem.anchor,
        quantification: typeof problem.quantification === 'string' ? JSON.parse(problem.quantification) : problem.quantification,
        assignment: typeof problem.assignment === 'string' ? JSON.parse(problem.assignment) : problem.assignment,
        priorityScore: problem.priority_score,
        problemId: problem.problem_id
      }));

      setAllProblems(parsedProblems);
      
      // Prospect problems (status = 'prospect') - need verification
      const prospect = parsedProblems.filter(p => p.status === 'prospect');
      setProspectProblems(prospect);
      
      // Verified problems (status = 'verified') - ready for solution
      const verified = parsedProblems.filter(p => p.status === 'verified');
      setVerifiedProblems(verified);
      
    } catch (error) {
      console.error('Failed to load problems:', error);
    } finally {
      setIsLoadingProblems(false);
    }
  };

  const loadSquadAssignments = async () => {
    try {
      const { data: assignedProblems, error } = await supabase
        .from('problem_bank')
        .select('*')
        .not('assigned_to_squad_id', 'is', null)
        .order('assigned_at', { ascending: false });

      if (error) throw error;

      const parsedAssignments = (assignedProblems || []).map(problem => {
        const assignment = typeof problem.assignment === 'string' ? JSON.parse(problem.assignment) : problem.assignment;
        const milestones = assignment?.milestones || [];
        const completedMilestones = milestones.filter((m: any) => m.completed).length;
        const progress = milestones.length > 0 ? (completedMilestones / milestones.length) * 100 : 0;
        
        return {
          id: problem.id,
          problemId: problem.problem_id,
          problemTitle: problem.title,
          squadId: problem.assigned_to_squad_id,
          squadName: problem.assigned_to_squad_name,
          assignedAt: problem.assigned_at,
          targetDate: assignment?.targetDeliveryDate,
          status: problem.status,
          progress: progress,
          milestones: milestones,
          assignedBy: problem.assigned_by,
          assignedByName: problem.assigned_by_name
        };
      });

      setAssignedToSquads(parsedAssignments);
    } catch (error) {
      console.error('Failed to load squad assignments:', error);
    }
  };

  const loadAnalysts = async () => {
    setIsLoadingAnalysts(true);
    try {
      const { data: usersData, error } = await supabase
        .from('users')
        .select('*')
        .in('role', ['analyst', 'senior_analyst']);

      if (error) throw error;

      // Problem Bank Analysts (for verification and solution)
      const problemBankAnalystsList = (usersData || []).filter(
        analyst => analyst.analyst_type === 'problem_bank'
      );
      setProblemBankAnalysts(problemBankAnalystsList);

      const analystsWithCounts = await Promise.all(
        (usersData || []).map(async (analyst) => {
          const { data: assignedProblems } = await supabase
            .from('problem_bank')
            .select('id, status, assignment')
            .eq('assigned_analyst_id', analyst.id);

          const completed = assignedProblems?.filter(p => p.status === 'solved') || [];
          const assigned = assignedProblems?.length || 0;
          
          const avgCompletionTime = completed.length > 0 ? Math.floor(Math.random() * 14) + 3 : 0;

          return {
            id: analyst.id,
            name: analyst.name,
            email: analyst.email,
            role: analyst.role,
            analyst_type: analyst.analyst_type,
            assignedProblemsCount: assigned,
            completedProblemsCount: completed.length,
            averageCompletionTime: avgCompletionTime,
            currentWorkload: assigned - completed.length,
          };
        })
      );

      setAnalysts(analystsWithCounts);
    } catch (error) {
      console.error('Failed to load analysts:', error);
    } finally {
      setIsLoadingAnalysts(false);
    }
  };

  const loadPendingSubmissions = async () => {
    try {
      const { data, error } = await supabase
        .from('external_submissions')
        .select('*')
        .in('status', ['pending', 'under_review'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPendingSubmissions(data || []);
    } catch (error) {
      console.error('Failed to load pending submissions:', error);
    }
  };

  const loadDeletionRequests = async () => {
    setIsLoadingRequests(true);
    try {
      const { data, error } = await supabase
        .from('problem_deletion_requests')
        .select('*')
        .eq('status', 'pending')
        .order('requested_at', { ascending: false });
      
      if (error) throw error;
      setDeletionRequests(data || []);
    } catch (error) {
      console.error('Error loading deletion requests:', error);
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const handleAssignForVerification = async () => {
    if (!selectedProblemForVerification || !selectedAnalystForVerification) {
      toast({
        title: 'Error',
        description: 'Please select both a problem and an analyst',
        variant: 'destructive'
      });
      return;
    }

    setAssigningVerification(true);
    try {
      const { error } = await supabase
        .from('problem_bank')
        .update({ 
          assigned_analyst_id: selectedAnalystForVerification,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedProblemForVerification);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Problem assigned for verification successfully'
      });

      setSelectedProblemForVerification('');
      setSelectedAnalystForVerification('');
      loadAllProblems();
    } catch (error) {
      console.error('Error assigning problem:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign problem',
        variant: 'destructive'
      });
    } finally {
      setAssigningVerification(false);
    }
  };

  const handleAssignForSolution = async () => {
    if (!selectedProblemForSolution || !selectedAnalystForSolution) {
      toast({
        title: 'Error',
        description: 'Please select both a problem and an analyst',
        variant: 'destructive'
      });
      return;
    }

    setAssigningSolution(true);
    try {
      const { error } = await supabase
        .from('problem_bank')
        .update({ 
          assigned_analyst_id: selectedAnalystForSolution,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedProblemForSolution);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Problem assigned for solution development successfully'
      });

      setSelectedProblemForSolution('');
      setSelectedAnalystForSolution('');
      loadAllProblems();
    } catch (error) {
      console.error('Error assigning problem:', error);
      toast({
        title: 'Error',
        description: 'Failed to assign problem',
        variant: 'destructive'
      });
    } finally {
      setAssigningSolution(false);
    }
  };

  const handleReviewDeletionRequest = async () => {
    if (!selectedRequest) return;
    
    setIsProcessing(true);
    try {
      if (reviewDecision === 'approve') {
        const { error: deleteError } = await supabase
          .from('problem_bank')
          .delete()
          .eq('id', selectedRequest.problem_id);
        
        if (deleteError) throw deleteError;
      }
      
      const { error: updateError } = await supabase
        .from('problem_deletion_requests')
        .update({
          status: reviewDecision === 'approve' ? 'approved' : 'rejected',
          reviewed_by: user?.id,
          reviewed_at: new Date().toISOString(),
          review_notes: reviewNotes
        })
        .eq('id', selectedRequest.id);
      
      if (updateError) throw updateError;
      
      toast({
        title: reviewDecision === 'approve' ? 'Problem Deleted' : 'Request Denied',
        description: `The deletion request has been ${reviewDecision === 'approve' ? 'approved and the problem deleted' : 'rejected'}.`,
      });
      
      setReviewDialogOpen(false);
      setSelectedRequest(null);
      setReviewNotes('');
      loadDeletionRequests();
      loadAllData();
      
    } catch (error) {
      console.error('Error reviewing deletion request:', error);
      toast({
        title: 'Error',
        description: 'Failed to process deletion request',
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getDashboardStats = () => {
    const totalProblems = allProblems.length;
    const prospectCount = allProblems.filter(p => p.status === 'prospect').length;
    const verifiedCount = allProblems.filter(p => p.status === 'verified').length;
    const assignedToSquadsCount = assignedToSquads.length;
    const completedProblems = allProblems.filter(p => p.status === 'solved').length;
    const pendingSubmissionsCount = pendingSubmissions.length;

    return {
      totalProblems,
      prospectCount,
      verifiedCount,
      assignedToSquads: assignedToSquadsCount,
      completedProblems,
      pendingSubmissions: pendingSubmissionsCount,
      activeAnalysts: analysts.filter(a => a.currentWorkload > 0).length,
      totalAnalysts: analysts.length,
    };
  };

  const stats = getDashboardStats();

  return (
    <div className="w-full min-h-screen bg-background">
      <div className="w-full px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Welcome back, {user?.name}!</h1>
          <p className="text-muted-foreground mt-1">
            Senior Analyst Dashboard - Oversee problem assignments, track squad progress, and manage deletion requests.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard title="Completed" value={stats.completedProblems.toString()} description="Solutions delivered" icon={CheckSquare} color="bg-emerald-500" />
          <StatCard title="Pending Review" value={stats.pendingSubmissions.toString()} description="External submissions" icon={Clock} color="bg-orange-500" />
          <StatCard title="Active Analysts" value={stats.activeAnalysts.toString()} description={`of ${stats.totalAnalysts} total`} icon={TrendingUp} color="bg-cyan-500" />
          <StatCard title="Deletion Requests" value={deletionRequests.length.toString()} description="Pending approval" icon={Trash2} color="bg-red-500" />
        </div>

        {/* ADMIN DASHBOARD STYLE CARDS - Assign for Verification & Assign for Solution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Assign for Verification Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-blue-600" />
                Assign for Verification
              </CardTitle>
              <CardDescription>
                Assign prospect problems to Problem Bank Analysts for verification
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Problem to Verify</Label>
                <Select value={selectedProblemForVerification} onValueChange={setSelectedProblemForVerification}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a problem to verify..." />
                  </SelectTrigger>
                  <SelectContent>
                    {prospectProblems.length === 0 ? (
                      <SelectItem value="" disabled>No problems awaiting verification</SelectItem>
                    ) : (
                      prospectProblems.map(problem => (
                        <SelectItem key={problem.id} value={problem.id}>
                          {problem.problem_id} - {problem.title?.substring(0, 50)}...
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {prospectProblems.length} problems awaiting verification
                </p>
              </div>

              <div className="space-y-2">
                <Label>Assign to Problem Bank Analyst</Label>
                <Select value={selectedAnalystForVerification} onValueChange={setSelectedAnalystForVerification}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a problem bank analyst..." />
                  </SelectTrigger>
                  <SelectContent>
                    {problemBankAnalysts.length === 0 ? (
                      <SelectItem value="" disabled>No problem bank analysts available</SelectItem>
                    ) : (
                      problemBankAnalysts.map(analyst => (
                        <SelectItem key={analyst.id} value={analyst.id}>
                          {analyst.name} ({analyst.email})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {problemBankAnalysts.length} problem bank analysts available
                </p>
              </div>

              <Button 
                onClick={handleAssignForVerification} 
                disabled={!selectedProblemForVerification || !selectedAnalystForVerification || assigningVerification}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {assigningVerification ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Assigning...</>
                ) : (
                  'Assign for Verification'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Assign for Solution Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5 text-green-600" />
                Assign for Solution
              </CardTitle>
              <CardDescription>
                Assign verified problems to Problem Bank Analysts for solution development
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Select Problem to Solve</Label>
                <Select value={selectedProblemForSolution} onValueChange={setSelectedProblemForSolution}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a verified problem..." />
                  </SelectTrigger>
                  <SelectContent>
                    {verifiedProblems.length === 0 ? (
                      <SelectItem value="" disabled>No verified problems ready for solution</SelectItem>
                    ) : (
                      verifiedProblems.map(problem => (
                        <SelectItem key={problem.id} value={problem.id}>
                          {problem.problem_id} - {problem.title?.substring(0, 50)}...
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {verifiedProblems.length} verified problem{verifiedProblems.length !== 1 ? 's' : ''} ready for solution
                </p>
              </div>

              <div className="space-y-2">
                <Label>Assign to Problem Bank Analyst</Label>
                <Select value={selectedAnalystForSolution} onValueChange={setSelectedAnalystForSolution}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a problem bank analyst..." />
                  </SelectTrigger>
                  <SelectContent>
                    {problemBankAnalysts.length === 0 ? (
                      <SelectItem value="" disabled>No problem bank analysts available</SelectItem>
                    ) : (
                      problemBankAnalysts.map(analyst => (
                        <SelectItem key={analyst.id} value={analyst.id}>
                          {analyst.name} ({analyst.email})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {problemBankAnalysts.length} problem bank analysts available
                </p>
              </div>

              <Button 
                onClick={handleAssignForSolution} 
                disabled={!selectedProblemForSolution || !selectedAnalystForSolution || assigningSolution}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {assigningSolution ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Assigning...</>
                ) : (
                  'Assign for Solution'
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Active Squad Assignments */}
        <Card className="w-full mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Users className="h-5 w-5 text-indigo-600" />
              Active Squad Assignments
            </CardTitle>
            <CardDescription>Problems currently being solved by squads</CardDescription>
          </CardHeader>
          <CardContent>
            {assignedToSquads.length === 0 ? (
              <div className="text-center py-12">
                <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground text-lg">No problems assigned to squads yet</p>
                <p className="text-sm text-muted-foreground mt-2">Business Analysts will assign problems to squads</p>
              </div>
            ) : (
              <div className="space-y-4">
                {assignedToSquads.map((assignment) => {
                  const daysRemaining = (() => {
                    if (!assignment.targetDate) return null;
                    const target = new Date(assignment.targetDate);
                    const now = new Date();
                    const diffTime = target.getTime() - now.getTime();
                    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                  })();
                  const isOverdue = daysRemaining !== null && daysRemaining < 0;
                  
                  return (
                    <div 
                      key={assignment.id} 
                      className="p-5 bg-muted/30 rounded-lg hover:bg-muted/50 cursor-pointer transition-all hover:shadow-md border-l-4 border-l-indigo-500"
                      onClick={() => navigate(`/problem-bank/problem/${assignment.id}/progress`)}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <Badge variant="outline" className="text-xs font-mono">{assignment.problemId}</Badge>
                            <Badge className={assignment.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}>
                              {assignment.status === 'in_progress' ? 'In Progress' : 'Active'}
                            </Badge>
                            {assignment.targetDate && (
                              <Badge variant={isOverdue ? "destructive" : "outline"} className={isOverdue ? "bg-red-100 text-red-700" : ""}>
                                {isOverdue ? `${Math.abs(daysRemaining)} days overdue` : `${daysRemaining} days remaining`}
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-base line-clamp-2">{assignment.problemTitle}</h3>
                          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                            <Users className="h-3.5 w-3.5" />
                            Squad: {assignment.squadName}
                          </p>
                          {assignment.assignedByName && (
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                              <UserCheck className="h-3 w-3" />
                              Assigned by: {assignment.assignedByName}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" className="shrink-0">
                          <Eye className="h-4 w-4 mr-1" />Track
                        </Button>
                      </div>
                      
                      <div className="mt-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Overall Progress</span>
                          <span className="font-medium">{Math.round(assignment.progress)}%</span>
                        </div>
                        <Progress value={assignment.progress} className="h-2" />
                      </div>
                      
                      {assignment.milestones && assignment.milestones.length > 0 && (
                        <div className="mt-4 pt-3 border-t">
                          <p className="text-xs font-medium text-muted-foreground mb-2">Milestone Progress:</p>
                          <div className="flex flex-wrap gap-2">
                            {assignment.milestones.slice(0, 3).map((milestone, idx) => (
                              <Badge key={idx} variant={milestone.completed ? "default" : "secondary"} className="text-xs">
                                {milestone.completed ? "✓" : "○"} {milestone.description?.length > 40 ? milestone.description.slice(0, 40) + "..." : milestone.description}
                              </Badge>
                            ))}
                            {assignment.milestones.length > 3 && (
                              <Badge variant="outline" className="text-xs">+{assignment.milestones.length - 3} more</Badge>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {assignment.targetDate && (
                        <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Target delivery: {new Date(assignment.targetDate).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="w-full mb-8">
          <CardHeader>
            <CardTitle className="text-xl">Quick Actions</CardTitle>
            <CardDescription>Common tasks and shortcuts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/problem-bank/new')}>
                <Plus className="h-6 w-6" />
                <span className="text-sm">New Problem</span>
              </Button>
              <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/problem-bank/submissions')}>
                <Clock className="h-6 w-6 text-yellow-500" />
                <span className="text-sm">Submissions</span>
              </Button>
              <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/problem-bank')}>
                <Database className="h-6 w-6" />
                <span className="text-sm">Problem Bank</span>
              </Button>
              <Button variant="outline" className="h-24 flex-col gap-2" onClick={() => navigate('/analyst/performance')}>
                <BarChart3 className="h-6 w-6" />
                <span className="text-sm">Analytics</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Pending Submissions Summary */}
        {pendingSubmissions.length > 0 && (
          <Card className="w-full mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Clock className="h-5 w-5 text-yellow-500" />
                Pending Submissions
              </CardTitle>
              <CardDescription>Need your review ({pendingSubmissions.length})</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pendingSubmissions.slice(0, 3).map((submission) => (
                  <div 
                    key={submission.id} 
                    className="p-3 hover:bg-muted rounded-lg cursor-pointer transition-colors border-l-2 border-l-yellow-500"
                    onClick={() => navigate(`/problem-bank/submissions/review/${submission.id}`)}
                  >
                    <p className="text-sm font-medium line-clamp-1">{submission.problem_title}</p>
                    <p className="text-xs text-muted-foreground mt-1">From: {submission.submitter_name || 'Anonymous'}</p>
                    <p className="text-xs text-muted-foreground">Submitted: {new Date(submission.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
                {pendingSubmissions.length > 3 && (
                  <Button variant="link" className="w-full mt-2" onClick={() => navigate('/problem-bank/submissions')}>
                    View all {pendingSubmissions.length} submissions <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Deletion Requests Section */}
        <Card className="w-full">
          <CardHeader>
            <CardTitle>Pending Deletion Requests</CardTitle>
            <CardDescription>Review and approve or reject problem deletion requests from Problem Bank Analysts</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingRequests ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : deletionRequests.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p className="text-muted-foreground">No pending deletion requests</p>
              </div>
            ) : (
              <div className="space-y-4">
                {deletionRequests.map((request) => (
                  <Card key={request.id} className="border-l-4 border-l-red-500">
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start flex-wrap gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <Badge variant="destructive">Pending Review</Badge>
                            <span className="text-xs text-muted-foreground">
                              Requested: {new Date(request.requested_at).toLocaleDateString()}
                            </span>
                          </div>
                          <h3 className="font-semibold text-lg">{request.problem_title}</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            Requested by: {request.requested_by_name}
                          </p>
                          <div className="mt-3 p-3 bg-muted/30 rounded-lg">
                            <p className="text-sm font-medium">Reason for deletion:</p>
                            <p className="text-sm mt-1">{request.reason}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/problem-bank/problem/${request.problem_id}`)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View Problem
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setReviewDecision('approve');
                              setReviewDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Approve Delete
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setReviewDecision('reject');
                              setReviewDialogOpen(true);
                            }}
                            className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Deny
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Review Deletion Request Dialog */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{reviewDecision === 'approve' ? 'Approve Deletion' : 'Deny Deletion Request'}</DialogTitle>
              <DialogDescription>
                {reviewDecision === 'approve' ? 'Are you sure you want to delete this problem? This action cannot be undone.' : 'Please provide a reason for denying this deletion request.'}
              </DialogDescription>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4 py-4">
                <div className="bg-muted/30 p-4 rounded-lg">
                  <p className="font-medium">{selectedRequest.problem_title}</p>
                  <p className="text-sm text-muted-foreground mt-1">Requested by: {selectedRequest.requested_by_name}</p>
                  <p className="text-sm text-muted-foreground mt-2">Reason: {selectedRequest.reason}</p>
                </div>
                <div className="space-y-2">
                  <Label>Review Notes</Label>
                  <Textarea value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder={reviewDecision === 'approve' ? 'Optional notes about approval...' : 'Reason for denying this request...'} rows={3} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleReviewDeletionRequest} disabled={isProcessing} className={reviewDecision === 'approve' ? 'bg-red-600 hover:bg-red-700' : 'bg-yellow-600 hover:bg-yellow-700'}>
                {isProcessing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</> : reviewDecision === 'approve' ? <><Trash2 className="h-4 w-4 mr-2" />Delete Problem</> : <><XCircle className="h-4 w-4 mr-2" />Deny Request</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}